import { useQuery } from '@tanstack/react-query'
import { tenderService } from '@/lib/api/services/tender'
import type { PaginationParams, TenderFilterParams } from '@/lib/api/types'

// Query keys factory
export const tenderKeys = {
  all: ['tenders'] as const,
  lists: () => [...tenderKeys.all, 'list'] as const,
  list: (params: PaginationParams, filters: TenderFilterParams) =>
    [...tenderKeys.lists(), params, filters] as const,
  searches: () => [...tenderKeys.all, 'search'] as const,
  search: (searchTerm: string, params: PaginationParams) =>
    [...tenderKeys.searches(), searchTerm, params] as const,
  details: () => [...tenderKeys.all, 'detail'] as const,
  detail: (id: string) => [...tenderKeys.details(), id] as const,
}

/**
 * Hook to fetch paginated tenders with filters
 */
export const useTenders = (
  params: PaginationParams = { page: 1, size: 10 },
  filters: TenderFilterParams = {}
) => {
  return useQuery({
    queryKey: tenderKeys.list(params, filters),
    queryFn: () => tenderService.getTenders(params, filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Hook to search tenders with pagination
 */
export const useSearchTenders = (
  searchTerm: string,
  params: PaginationParams = { page: 1, size: 10 }
) => {
  return useQuery({
    queryKey: tenderKeys.search(searchTerm, params),
    queryFn: () => tenderService.searchTenders(searchTerm, params),
    enabled: searchTerm.trim().length > 0,
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}

/**
 * Hook to fetch a single tender by ID
 */
export const useTender = (tenderId?: string) => {
  return useQuery({
    queryKey: tenderKeys.detail(tenderId || ''),
    queryFn: () => {
      if (!tenderId) {
        throw new Error('Tender ID is required')
      }
      return tenderService.getTenderById(tenderId)
    },
    enabled: !!tenderId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Hook to fetch bid types
 */
export const useBidTypes = () => {
  return useQuery({
    queryKey: [...tenderKeys.all, 'bidTypes'],
    queryFn: () => tenderService.getBidTypes(),
    staleTime: 30 * 60 * 1000, // 30 minutes - bid types don't change often
  })
}


